import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Camera, Trash2, Mic, MicOff, CloudOff, RefreshCw } from "lucide-react";
import { PhotoAnnotator } from "@/components/PhotoAnnotator";
import { addPendingPhoto, getPendingPhotos, updatePendingPhoto, removePendingPhoto } from "@/lib/offlineStore";
import { drain, onSyncChange } from "@/lib/syncEngine";

// ─── Reporte Fotográfico MIP — componente centralizado (técnico + auditor) ───
const PHOTO_ZONES = [
    { key: "verde",    label: "Zona Verde",    color: "bg-green-100 text-green-800 border-green-300",   dot: "bg-green-500" },
    { key: "amarilla", label: "Zona Amarilla", color: "bg-yellow-100 text-yellow-800 border-yellow-300", dot: "bg-yellow-500" },
    { key: "roja",     label: "Zona Roja",     color: "bg-red-100 text-red-800 border-red-300",          dot: "bg-red-500" },
];
const PHOTO_AREAS = {
    verde:    ["Perímetro exterior","Jardines / áreas verdes","Estacionamiento","Contenedores de basura","Zona de carga / descarga","Drenajes externos","Luminarias exteriores","Otra"],
    amarilla: ["Cocina / área de preparación","Bodega de alimentos","Cuarto de basura interior","Vestuarios / casilleros","Cuarto de máquinas","Área con humedad / calor","Otra"],
    roja:     ["Salón / área de atención","Baños de clientes","Pasillos de exhibición","Recepción / área administrativa","Comedor","Otra"],
};
const MAX_PHOTOS_MIP = 15;

function blobToDataURL(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// Memory-safe image compression for low-RAM phones.
// Uses createImageBitmap (decodes more efficiently than <img>) and releases the
// full-resolution bitmap immediately with .close() to avoid out-of-memory tab
// crashes (blank white screen) on lower-end devices. Falls back to <img>+canvas.
async function compressImageMIP(file, maxPx = 1024, quality = 0.72) {
    const fitCanvas = (srcW, srcH, drawFn) => {
        let width = srcW, height = srcH;
        if (width > maxPx || height > maxPx) {
            if (width > height) { height = Math.round((height * maxPx) / width); width = maxPx; }
            else { width = Math.round((width * maxPx) / height); height = maxPx; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        drawFn(canvas.getContext("2d"), width, height);
        return new Promise((resolve, reject) => {
            canvas.toBlob(async (blob) => {
                try {
                    const uri = blob ? await blobToDataURL(blob) : null;
                    canvas.width = 0; canvas.height = 0; // release canvas memory
                    if (uri) resolve(uri); else reject(new Error("toBlob null"));
                } catch (e) { reject(e); }
            }, "image/jpeg", quality);
        });
    };

    // Preferred path: createImageBitmap (lower peak memory, honors EXIF orientation)
    if (typeof createImageBitmap === "function") {
        let bmp = null;
        try {
            try {
                bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
            } catch {
                bmp = await createImageBitmap(file); // older engines ignore options
            }
            const result = await fitCanvas(bmp.width, bmp.height, (ctx, w, h) => ctx.drawImage(bmp, 0, 0, w, h));
            return result;
        } catch (e) {
            // fall through to <img> fallback
        } finally {
            if (bmp && bmp.close) { try { bmp.close(); } catch {} }
        }
    }

    // Fallback: <img> + canvas
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = async () => {
            try {
                const uri = await fitCanvas(img.width, img.height, (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h));
                URL.revokeObjectURL(url);
                resolve(uri);
            } catch (e) { URL.revokeObjectURL(url); reject(e); }
        };
        img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
        img.src = url;
    });
}

export function PhotoReportMIP({ serviceId }) {
    const [activeZone, setActiveZone] = useState("verde");
    const [photos, setPhotos] = useState([]);          // uploaded (server) photos
    const [pending, setPending] = useState([]);        // captured offline, awaiting upload
    const [uploading, setUploading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [listening, setListening] = useState(null);
    const [pendingAnnotation, setPendingAnnotation] = useState(null); // dataUri waiting for annotation
    const recognitionRef = useRef(null);
    const fileInputRef = useRef(null);
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    // Keeps the locally-captured image (data URI) per photo id so the thumbnail
    // never breaks when a background sync reloads the report and the network
    // fetch of the stored photo momentarily fails (flaky mobile connection).
    const previewsRef = useRef({});

    const loadReport = () => api.get(`/photo-reports/${serviceId}`)
        .then((r) => {
            const rows = (r.data?.photos || []).map((p) => ({ ...p, preview: p.preview || previewsRef.current[p.id] }));
            setPhotos(rows);
        })
        .catch(() => {});
    const loadPending = () => getPendingPhotos(serviceId).then((rows) => setPending(rows || [])).catch(() => {});

    useEffect(() => {
        loadReport();
        loadPending();
        // Refresh when the sync engine uploads queued photos in the background
        const off = onSyncChange(() => { loadReport(); loadPending(); });
        return off;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceId]);

    const totalCount = photos.length + pending.length;
    const zonePhotos = (zone) => [...photos, ...pending].filter((p) => p.zone === zone);
    const isPending = (photoId) => pending.some((p) => p.id === photoId);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (totalCount >= MAX_PHOTOS_MIP) { toast.error(`Límite de ${MAX_PHOTOS_MIP} fotos`); return; }
        setUploading(true);
        try {
            const dataUri = await compressImageMIP(file);
            setPendingAnnotation(dataUri);
        } catch (err) { toast.error("Error al procesar foto"); }
        finally { setUploading(false); e.target.value = ""; }
    };

    const handleAnnotationConfirm = async (annotatedDataUri) => {
        setPendingAnnotation(null);
        setUploading(true);
        const meta = { zone: activeZone, area: PHOTO_AREAS[activeZone][0], custom_area: "", description: "", is_reincident: false };
        // Try to upload immediately when online
        if (navigator.onLine) {
            try {
                const res = await api.post(`/photo-reports/${serviceId}/photos`, { ...meta, data_uri: annotatedDataUri });
                previewsRef.current[res.data.photo.id] = annotatedDataUri;
                setPhotos((prev) => [...prev, { ...res.data.photo, preview: annotatedDataUri }]);
                setEditId(res.data.photo.id);
                toast.success("Foto agregada");
                setUploading(false);
                return;
            } catch (err) {
                const status = err?.response?.status;
                if (status && status >= 400 && status < 500) {
                    toast.error(err?.response?.data?.detail || "Error al subir foto");
                    setUploading(false);
                    return;
                }
                // network error -> fall through to offline path
            }
        }
        // Offline (or network failed): store locally, upload later
        const localId = `pp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const record = { id: localId, serviceId, ...meta, data_uri: annotatedDataUri, created_at: new Date().toISOString(), _pending: true };
        try {
            await addPendingPhoto(record);
            setPending((prev) => [...prev, record]);
            setEditId(localId);
            toast.message("Foto guardada sin conexión", { description: "Se subirá automáticamente al recuperar la señal." });
        } catch (err) {
            toast.error("No se pudo guardar la foto localmente");
        } finally { setUploading(false); }
    };

    const handleAnnotationCancel = () => setPendingAnnotation(null);

    const syncNow = async () => {
        setSyncing(true);
        try {
            await drain();
            await loadReport();
            await loadPending();
        } finally { setSyncing(false); }
    };

    const updatePhoto = async (photoId, fields) => {
        if (isPending(photoId)) {
            setPending((prev) => prev.map((p) => p.id === photoId ? { ...p, ...fields } : p));
            try { await updatePendingPhoto(photoId, fields); } catch { toast.error("Error al guardar"); }
            return;
        }
        setPhotos((prev) => prev.map((p) => p.id === photoId ? { ...p, ...fields } : p));
        try { await api.patch(`/photo-reports/${serviceId}/photos/${photoId}`, fields); }
        catch { toast.error("Error al guardar"); }
    };

    const deletePhoto = async (photoId) => {
        if (isPending(photoId)) {
            try { await removePendingPhoto(photoId); } catch {}
            setPending((prev) => prev.filter((p) => p.id !== photoId));
            if (editId === photoId) setEditId(null);
            return;
        }
        try {
            await api.delete(`/photo-reports/${serviceId}/photos/${photoId}`);
            setPhotos((prev) => prev.filter((p) => p.id !== photoId));
            if (editId === photoId) setEditId(null);
        } catch { toast.error("Error al eliminar"); }
    };

    const toggleVoice = (photoId) => {
        if (!("webkitSpeechRecognition" in window || "SpeechRecognition" in window)) { toast.error("Dictado no disponible"); return; }
        if (listening === photoId) { recognitionRef.current?.stop(); setListening(null); return; }
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        const rec = new SR();
        rec.lang = "es-SV"; rec.continuous = false; rec.interimResults = false;
        rec.onresult = (ev) => {
            const t = ev.results[0][0].transcript;
            const cur = [...photos, ...pending].find((p) => p.id === photoId)?.description || "";
            updatePhoto(photoId, { description: cur ? `${cur} ${t}` : t });
        };
        rec.onerror = () => setListening(null);
        rec.onend = () => setListening(null);
        rec.start(); recognitionRef.current = rec; setListening(photoId);
    };

    return (
        <div className="space-y-4">
            {pendingAnnotation && (
                <PhotoAnnotator
                    imageDataUri={pendingAnnotation}
                    onConfirm={handleAnnotationConfirm}
                    onCancel={handleAnnotationCancel}
                />
            )}

            <Card className="p-4">
                <div className="flex items-start justify-between mb-2">
                    <div>
                        <h2 className="font-semibold text-lg">Reporte fotográfico MIP</h2>
                        <p className="text-xs text-muted-foreground mt-0.5">Oportunidades de mejora — opcional. Máx. {MAX_PHOTOS_MIP} fotos.</p>
                    </div>
                    <Badge variant={totalCount >= MAX_PHOTOS_MIP ? "destructive" : "secondary"} className="text-xs shrink-0">
                        {totalCount}/{MAX_PHOTOS_MIP}
                    </Badge>
                </div>

                {pending.length > 0 && (
                    <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 mb-3" data-testid="pending-photos-banner">
                        <CloudOff className="w-4 h-4 text-amber-600 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-amber-800">{pending.length} foto{pending.length !== 1 ? "s" : ""} pendiente{pending.length !== 1 ? "s" : ""} de subir</p>
                            <p className="text-[10px] text-amber-700 leading-snug">Se guardaron en este dispositivo y se subirán solas al recuperar la señal.</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-8 text-xs gap-1 border-amber-400 text-amber-800 shrink-0"
                            onClick={syncNow} disabled={syncing} data-testid="sync-pending-photos-btn">
                            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
                            {syncing ? "Sincronizando..." : "Sincronizar ahora"}
                        </Button>
                    </div>
                )}

                <div className="flex border rounded-lg overflow-hidden mb-4">
                    {PHOTO_ZONES.map((z) => {
                        const count = zonePhotos(z.key).length;
                        return (
                            <button key={z.key} onClick={() => setActiveZone(z.key)}
                                data-testid={`zone-tab-${z.key}`}
                                className={`flex-1 py-2 text-xs font-semibold flex flex-col items-center gap-0.5 transition-colors ${activeZone === z.key ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted"}`}>
                                <span className="flex items-center gap-1">
                                    <span className={`w-2 h-2 rounded-full ${z.dot}`} />
                                    {z.label}
                                </span>
                                {count > 0 && <span className="text-[10px] opacity-70">{count} foto{count !== 1 ? "s" : ""}</span>}
                            </button>
                        );
                    })}
                </div>

                <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} data-testid="photo-file-input" />
                <Button variant="outline" className="w-full h-10 text-sm gap-2 border-dashed" onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || totalCount >= MAX_PHOTOS_MIP} data-testid="add-photo-btn">
                    {uploading ? <><span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />Procesando...</>
                        : <><Camera className="w-4 h-4" />Agregar foto — {PHOTO_ZONES.find((z) => z.key === activeZone)?.label}</>}
                </Button>
            </Card>

            {zonePhotos(activeZone).length === 0 && (
                <p className="text-center text-xs text-muted-foreground py-4">Sin fotos en esta zona. Toca el botón para agregar.</p>
            )}
            {zonePhotos(activeZone).map((photo) => {
                const zone = PHOTO_ZONES.find((z) => z.key === photo.zone);
                const pend = !!photo._pending;
                const imgSrc = photo.preview || photo.data_uri || `${API_URL}${photo.photo_url}`;
                return (
                    <Card key={photo.id} className="overflow-hidden" data-testid={`photo-card-${photo.id}`}>
                        <div className="flex gap-3 p-3">
                            <img src={imgSrc} alt="Hallazgo" className="w-20 h-20 object-cover rounded border shrink-0 cursor-pointer" onClick={() => setEditId(editId === photo.id ? null : photo.id)}
                                onError={(e) => {
                                    const el = e.currentTarget;
                                    // Retry the server image once (transient mobile-network failure) with a cache-buster.
                                    if (!photo.preview && photo.photo_url && !el.dataset.retried) {
                                        el.dataset.retried = "1";
                                        const sep = photo.photo_url.includes("?") ? "&" : "?";
                                        el.src = `${API_URL}${photo.photo_url}${sep}cb=${Date.now()}`;
                                    }
                                }} />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded border ${zone?.color}`}>
                                        <span className={`w-2 h-2 rounded-full ${zone?.dot}`} />{zone?.label}
                                    </span>
                                    {pend && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700" data-testid={`photo-pending-badge-${photo.id}`}>
                                            <CloudOff className="w-3 h-3" /> Pendiente de subir
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs font-medium mt-1 truncate">{photo.custom_area || photo.area}</p>
                                {photo.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{photo.description}</p>}
                                {photo.is_reincident && <Badge variant="destructive" className="text-[10px] py-0 mt-1">Reincidente</Badge>}
                            </div>
                            <Button variant="ghost" size="icon" className="shrink-0 text-destructive h-8 w-8" onClick={() => deletePhoto(photo.id)} data-testid={`delete-photo-${photo.id}`}>
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                        {editId === photo.id && (
                            <div className="border-t px-3 pb-3 pt-2 space-y-3 bg-muted/30">
                                <div>
                                    <Label className="text-xs">Área</Label>
                                    <Select value={photo.area} onValueChange={(v) => updatePhoto(photo.id, { area: v })}>
                                        <SelectTrigger className="h-9 text-xs mt-1" data-testid={`area-select-${photo.id}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>{PHOTO_AREAS[photo.zone].map((a) => <SelectItem key={a} value={a} className="text-xs">{a}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                {photo.area === "Otra" && (
                                    <div>
                                        <Label className="text-xs">Especificar área</Label>
                                        <input className="mt-1 w-full h-9 rounded-md border bg-background px-3 text-xs" value={photo.custom_area || ""} onChange={(e) => updatePhoto(photo.id, { custom_area: e.target.value })} placeholder="Ej: Baño de empleados" />
                                    </div>
                                )}
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <Label className="text-xs">Descripción del hallazgo</Label>
                                        <Button variant="ghost" size="icon" className={`h-7 w-7 ${listening === photo.id ? "text-red-500" : "text-muted-foreground"}`} onClick={() => toggleVoice(photo.id)}>
                                            {listening === photo.id ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                                        </Button>
                                    </div>
                                    <Textarea rows={2} className="text-xs resize-none" value={photo.description || ""} onChange={(e) => updatePhoto(photo.id, { description: e.target.value })} placeholder="Describa el hallazgo..." />
                                </div>
                                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                                    <Label className="text-xs cursor-pointer">¿Es reincidente?</Label>
                                    <Switch checked={!!photo.is_reincident} onCheckedChange={(v) => updatePhoto(photo.id, { is_reincident: v })} />
                                </div>
                            </div>
                        )}
                    </Card>
                );
            })}
        </div>
    );
}

export default PhotoReportMIP;
